// Section 3 — permanent ring guidance + contextual "Optimization Path" tips.
// Copy is intentionally persistent (no disappearing tooltips).

export const RING_GUIDE = {
  locations: {
    title: "Spatial Viability",
    what: "Evaluates the site microclimate against crop requirements.",
    how: "Increase it by matching high-DLI zones with fruiting crops.",
  },
  systems: {
    title: "Hardware Efficiency",
    what: "Tracks structural and operational compatibility.",
    how: "Increase it by selecting systems suited to the chosen architectural zone.",
  },
  crops: {
    title: "Yield Target",
    what: "Progress toward the maximum biological output for the available area.",
    how: "Increase it by allocating area (m²) and selecting crops matched to the site's light.",
  },
  resources: {
    title: "Circularity Index",
    what: "Tracks closed-loop integration.",
    how: "Increase it by meeting water and nutrient demand with on-site waste recovery.",
  },
};

// Per-option optimization paths. Locations & systems keyed by id; crops keyed by
// group; resources keyed by category — with sensible fallbacks in getOptTip().
const TIPS = {
  locations: {
    rooftop: "High solar exposure with significant wind shear. Suits modular hydroponic pods or a rooftop greenhouse.",
    ground: "Deep-soil access and simple water routing, but lower light. Suits aquaponics or raised beds; add a water loop.",
    facade_s: "Receives the most annual sun. Well suited to fruiting crops on living walls or trellis vines.",
    facade_e: "Gentle morning light suited to leafy greens on living walls; add a water loop to sustain productivity.",
    facade_w: "Hot afternoon exposure. Use heat-tolerant crops on trellis vines and a rainwater loop for cooling irrigation.",
    facade_n: "Low light. Best for shade-tolerant greens or a green buffer. Use indoor systems for fruiting crops.",
    balcony: "Suited to compact planters and herbs. Pair with a small compost loop.",
    indoor: "Fully controlled light, temperature and humidity. Suits indoor vertical farming; add an energy loop to offset the electrical load.",
  },
  systems: {
    nft: "Requires continuous nutrient-film flow. Best paired with a rainwater or greywater recovery loop to offset water demand.",
    dwc: "High but stable water demand. Pair with a rainwater or greywater loop to close the water balance.",
    aeroponic_towers: "High yield per m² with elevated power draw. Pair with a solar or biogas energy loop to offset consumption.",
    aquaponics: "Fish and plants form a closed nutrient loop. Pair with a water loop and nutrient-demanding crops.",
    extensive_green_roof: "Lightweight, low-maintenance groundcover. Add a rainwater loop for passive irrigation.",
    intensive_green_roof: "Deeper substrate supports full crops but adds structural load. Pair with a compost loop for fertility.",
    rooftop_greenhouse: "Extends the growing season. Pair with an energy loop to cover heating and lighting.",
    rooftop_raised_beds: "Robust and productive. Add compost and rainwater loops to close the cycle.",
    living_walls: "Converts façades into growing area. Requires continuous irrigation; connect a water loop.",
    trellis_vines: "Performs well with vertical sun on south or west façades. Add a drip water loop for heavy fruit set.",
    balcony_planters: "Compact and quick to deploy. Add a small compost loop and match herbs or greens to the available light.",
  },
  cropsDefault: "Match the crop's DLI range to the site light. If flagged suboptimal, add supplemental lighting or relocate to a higher-DLI zone.",
  resources: {
    Water: "Rainwater and greywater loops raise the Circularity Index. Add one for each water-intensive system.",
    Energy: "Biogas and solar loops offset system power draw; gains scale with the system's electrical load.",
    Amendment: "Compost and digestate loops close the nutrient cycle. Pair with high-yield crops to recover the most mass.",
  },
  resourcesDefault: "Returns recovered water, nutrients, or energy to your systems, raising the Circularity Index.",
};

export function getOptTip(pillar, item) {
  if (!item) return null;
  if (pillar === "locations") return TIPS.locations[item.id] || null;
  if (pillar === "systems") return TIPS.systems[item.id] || null;
  if (pillar === "crops") return TIPS.cropsDefault;
  if (pillar === "resources") return TIPS.resources[item.category] || TIPS.resourcesDefault;
  return null;
}
